import { useEffect } from 'react';
import { paymentsApi } from '@/lib/auth_api';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

// Human-readable label for each bonus-addon SKU shown in the success toast.
const ADDON_LABELS = {
  book_launch_kit:          'Book Launch Kit',
  cover_credits_5:          '5 Cover Design Credits',
  cover_credits_12:         '12 Cover Design Credits',
  priority_queue_30d:       'Priority Queue · 30 days',
  adobe_canva_pass_30d:     'Adobe + Canva Pass · 30 days',
  zernio_author_pro_30d:    'Author Marketing Pro · 30 days',
  audiobook_credits_1:      '+1 Audiobook Run',
  audiobook_credits_3:      '+3 Audiobook Runs',
  audiobook_narration_1:    'Audiobook Narration credit',
  continuity_audit_1:       'Continuity Audit credit',
  bulk_draft_1:             'Bulk Novel Draft credit',
  script_conversion_1:      'Script Conversion credit',
  coaching_session_1:       'Writer Coaching Session',
  editorial_review_1:       'Human Editorial Review',
};

/**
 * Handles the Stripe Checkout return flow. When the URL carries
 * `?payment=success&session_id=…` or `?payment=cancelled`, it polls the
 * backend for completion, surfaces a toast, refreshes the auth user (so
 * the new token balance is loaded), and cleans up the URL.
 *
 * For narration_service checkouts the URL also carries
 * `narration_project={pid}`. On success we show a distinct toast and fire
 * `loom:open-narration` so the NarrationStudioDialog reopens automatically.
 *
 * For bonus-addon checkouts the backend uses `?bonus_addon=success&sku=…`
 * so this hook handles that branch too and fires `loom:addon-credited` so
 * any mounted BonusAddonsShelf or tool button can refresh its state.
 */
export function usePaymentReturn(onCredited) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const paymentFlag       = searchParams.get('payment');
    const bonusAddonFlag    = searchParams.get('bonus_addon');
    const sessionId         = searchParams.get('session_id');
    const addonSku          = searchParams.get('sku');
    const narrationProject  = searchParams.get('narration_project');

    // ── Bonus add-on return ──────────────────────────────────────────────────
    if (bonusAddonFlag) {
      if (bonusAddonFlag === 'cancelled') {
        toast.info('Purchase cancelled.');
        navigate('/', { replace: true });
        return;
      }
      if (bonusAddonFlag === 'success' && sessionId) {
        let cancelled = false;
        const poll = async (attempt = 0) => {
          if (cancelled || attempt > 12) return;
          try {
            const s = await paymentsApi.status(sessionId);
            if (s.payment_status === 'paid') {
              const label = ADDON_LABELS[addonSku] || addonSku || 'Add-on';
              toast.success(`${label} unlocked and credited to your account.`);
              onCredited?.();
              // Signal any mounted tool button or BonusAddonsShelf to refresh.
              window.dispatchEvent(new CustomEvent('loom:addon-credited', { detail: { sku: addonSku } }));
              navigate('/', { replace: true });
              return;
            }
            if (s.status === 'expired') {
              toast.error('Payment session expired.');
              navigate('/', { replace: true });
              return;
            }
          } catch (err) {
            if (attempt === 0) console.warn('[bonus-addon] status check failed', err);
          }
          setTimeout(() => poll(attempt + 1), 2000);
        };
        poll();
        return () => { cancelled = true; };
      }
    }

    if (!paymentFlag) return;

    if (paymentFlag === 'cancelled') {
      toast.info('Payment cancelled.');
      navigate('/', { replace: true });
      return;
    }
    if (paymentFlag === 'success' && sessionId) {
      let cancelled = false;
      const poll = async (attempt = 0) => {
        if (cancelled || attempt > 10) return;
        try {
          const s = await paymentsApi.status(sessionId);
          if (s.payment_status === 'paid') {
            // ── Narration unlock ──────────────────────────────────────────
            if (s.kind === 'narration_service' || narrationProject) {
              toast.success('Narration enabled! Your audiobook generation is unlocked.');
              onCredited?.();
              // Clean the URL, then fire the event so the dialog reopens.
              navigate('/', { replace: true });
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('loom:open-narration'));
              }, 400);
              return;
            }
            // ── Generic token/copyscape credit ───────────────────────────
            toast.success(`Payment complete · +${(s.tokens || 0).toLocaleString()} tokens credited.`);
            onCredited?.();
            navigate('/', { replace: true });
            return;
          }
          if (s.status === 'expired') {
            toast.error('Payment session expired.');
            navigate('/', { replace: true });
            return;
          }
        } catch (err) {
          if (attempt === 0) console.warn('[payments] status check failed', err);
        }
        setTimeout(() => poll(attempt + 1), 2000);
      };
      poll();
      return () => { cancelled = true; };
    }
  }, [searchParams, navigate, onCredited]);
}
